import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import QRCode from 'qrcode';
import { afterSchoolEducationProgramsContent } from '../content/educational/afterSchoolEducationProgramsContent';

const generatePDF = async (content, title, keywords) => {
  try {
    if (!content || !title || !keywords) {
      throw new Error('Missing required parameters');
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 25;
    const contentWidth = pageWidth - (margin * 2);
    const headerHeight = 35;
    const footerHeight = 12;
    const lineSpacing = 1.8;
    const paragraphSpacing = 0.6;
    const bulletIndent = 8;
    const numberIndent = 5;
    const titleSpacing = 12;
    const titleLineSpacing = 6;
    const sectionSpacing = 4;
    const contentSpacing = 3;
    const pageBreakThreshold = 10;

    // Generate QR Codes
    const qrCodeHomepage = await QRCode.toDataURL('https://causeandimpact.org');
    const qrCodeApply = await QRCode.toDataURL('https://causeandimpact.org/apply');

    // Header component
    const addHeader = () => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, 0, pageWidth, headerHeight, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(24);
      
      // Blue shadow effect
      doc.setTextColor(37, 99, 235);
      doc.text('Cause & Impact, Inc.', (pageWidth / 2) + 0.5, 20.1, { align: 'center' });
      
      // Green overlay
      doc.setTextColor(5, 150, 105);
      doc.text('Cause & Impact, Inc.', pageWidth / 2, 20, { align: 'center' });

      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      doc.text('Expert Resources for Nonprofit Success', pageWidth / 2, 30, { align: 'center' });

      doc.addImage(qrCodeHomepage, 'PNG', pageWidth - margin - 25, 10, 20, 20);
    };

    // Footer component
    const addFooter = (pageNumber) => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, 'F');

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(255, 255, 255);
      doc.text(
        'Cause & Impact, Inc. | EIN: 84-3918881 | www.causeandimpact.org',
        pageWidth / 2,
        pageHeight - 5,
        { align: 'center' }
      );
      doc.text(`Page ${pageNumber}`, pageWidth - margin, pageHeight - 5, { align: 'right' });
    };

    // Call to Action component
    const addCallToAction = (yPosition) => {
      const ctaHeight = 35;
      const qrSize = 22;
      doc.setFillColor(37, 99, 235);
      doc.roundedRect(margin, yPosition, contentWidth, ctaHeight, 4, 4, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(255, 255, 255);
      doc.text('Ready to start?', pageWidth / 2, yPosition + 13, { align: 'center' });

      doc.setFontSize(10);
      doc.text('Visit causeandimpact.org/apply', pageWidth / 2, yPosition + 22, { align: 'center' });

      doc.addImage(qrCodeApply, 'PNG', pageWidth - qrSize - margin - 6, yPosition + 6.5, qrSize, qrSize);
    };

    // Helper function to process markdown text
    const processMarkdown = (text) => {
      let segments = [];
      const boldRegex = /\*\*(.*?)\*\*/g;
      let lastIndex = 0;
      let match;
      
      while ((match = boldRegex.exec(text)) !== null) {
        if (match.index > lastIndex) {
          segments.push({
            text: text.substring(lastIndex, match.index),
            bold: false
          });
        }
        segments.push({
          text: match[1],
          bold: true
        });
        lastIndex = match.index + match[0].length;
      }
      
      if (lastIndex < text.length) {
        segments.push({
          text: text.substring(lastIndex),
          bold: false
        });
      }
      
      return segments;
    };

    // Update processList function to handle numbered lists and bold text
    const processList = (text, yPosition) => {
      const lines = text.split('\n').filter(line => line.trim() !== '');
      let currentY = yPosition;
      
      lines.forEach(line => {
        if (line.trim() === '') return;
        
        const trimmedLine = line.trim();
        
        // Handle numbered lists with bold (e.g., "**1. Title**")
        const numberedBoldMatch = trimmedLine.match(/^\*\*(\d+\.\s*.*?)\*\*$/);
        if (numberedBoldMatch) {
          doc.setFont('helvetica', 'bold');
          doc.text(numberedBoldMatch[1], margin + numberIndent, currentY);
          currentY += 5; // Increased spacing after numbered title
          return;
        }
        
        if (trimmedLine.startsWith('-')) {
          const bulletText = trimmedLine.substring(1).trim();
          if (bulletText) {
            // Handle bold prefixes in bullet points (e.g., "- **Title**: Content")
            const boldPrefixMatch = bulletText.match(/^\*\*(.*?)\*\*:\s*(.*)$/);
            if (boldPrefixMatch) {
              const [, boldPart, restPart] = boldPrefixMatch;
              const x = margin + bulletIndent + 6;
              
              // Render bold prefix
              doc.setFont('helvetica', 'bold');
              doc.text(boldPart + ':', x, currentY);
              const boldWidth = doc.getTextWidth(boldPart + ': ');
              
              // Render rest of text
              doc.setFont('helvetica', 'normal');
              const lines = doc.splitTextToSize(restPart, contentWidth - (bulletIndent + 15) - boldWidth);
              doc.text(lines, x + boldWidth, currentY);
              currentY += (lines.length * 3.2) + 2;
            } else {
              // Regular bullet point
              doc.text('•', margin + bulletIndent, currentY);
              const x = margin + bulletIndent + 6;
              
              doc.setFont('helvetica', 'normal');
              const lines = doc.splitTextToSize(bulletText, contentWidth - (bulletIndent + 15));
              doc.text(lines, x, currentY);
              currentY += (lines.length * 3.2) + 2;
            }
          }
        } else {
          const lines = doc.splitTextToSize(trimmedLine, contentWidth);
          doc.text(lines, margin, currentY);
          currentY += lines.length * 3.2;
        }
      });
      
      return currentY;
    };

    // Content generation with improved formatting
    const addContent = () => {
      let yPosition = headerHeight + 12;
      let pageNumber = 1;

      // Add title with increased spacing
      doc.setFontSize(20);
      doc.setTextColor(37, 99, 235);
      doc.setFont('helvetica', 'bold');
      const titleLines = doc.splitTextToSize(title, contentWidth);
      doc.text(titleLines, pageWidth / 2, yPosition + 2, { align: 'center' });
      
      // Increase spacing after title
      yPosition += (titleLines.length * titleLineSpacing) + titleSpacing;

      // Add subtitle (green heading) with more spacing
      doc.setFontSize(16);
      doc.setTextColor(5, 150, 105);
      const subtitleLines = doc.splitTextToSize(content[0].heading, contentWidth);
      doc.text(subtitleLines, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += (subtitleLines.length * 5) + 5;

      // Process all sections including the first one
      content.forEach((section, index) => {
        // Skip the first section since we already processed it as subtitle
        if (index === 0) return;

        // Calculate total section height
        const sectionHeadingHeight = 10;
        const contentLines = doc.splitTextToSize(section.text, contentWidth);
        const sectionContentHeight = contentLines.length * 3.2;
        const totalSectionHeight = sectionHeadingHeight + sectionContentHeight + 6;

        if (yPosition + totalSectionHeight > pageHeight - footerHeight - pageBreakThreshold) {
          doc.addPage();
          pageNumber++;
          addHeader();
          addFooter(pageNumber);
          yPosition = headerHeight + 12;
        }

        // Add section heading
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.setTextColor(5, 150, 105);
        doc.text(section.heading, margin, yPosition);
        yPosition += 8;

        // Process section content
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(31, 41, 55);

        const paragraphs = section.text.split('\n\n');
        paragraphs.forEach((paragraph, pIndex) => {
          const lines = doc.splitTextToSize(paragraph, contentWidth - (margin * 2));
          const paragraphHeight = lines.length * 3.2 + 3;

          if (yPosition + paragraphHeight > pageHeight - footerHeight - pageBreakThreshold) {
            doc.addPage();
            pageNumber++;
            addHeader();
            addFooter(pageNumber);
            yPosition = headerHeight + 12;
          }

          yPosition = processList(paragraph, yPosition);

          if (pIndex < paragraphs.length - 1) {
            yPosition += contentSpacing;
          }
        });

        yPosition += 5;
      });

      // Add CTA at the end of all content
      const ctaHeight = 35;
      
      if (yPosition + ctaHeight > pageHeight - footerHeight - 12) {
        doc.addPage();
        pageNumber++;
        addHeader();
        addFooter(pageNumber);
        yPosition = headerHeight + 12;
      }
      
      yPosition += 3;
      addCallToAction(yPosition);
    };

    // Generate PDF
    addHeader();
    addFooter(1);
    addContent();

    // Add metadata for SEO and document properties
    doc.setProperties({
      title: title,
      subject: 'After-School and Education Programs Expert Guide',
      keywords: keywords.join(', '),
      author: 'Cause & Impact',
      creator: 'Cause & Impact',
      description: 'Comprehensive expert guide for after-school and education programs, covering compliance, funding, curriculum planning, and stakeholder engagement.',
      modDate: new Date()
    });

    return doc;
  } catch (error) {
    console.error('Error in generatePDF:', error);
    throw error;
  }
};

export const generateAfterSchoolEducationProgramsOverview = async () => {
  try {
    console.log('Starting after school PDF generation'); // Debug log

    // Validate content
    if (!afterSchoolEducationProgramsContent) {
      throw new Error('Content is missing');
    }

    if (!afterSchoolEducationProgramsContent.sections || 
        !Array.isArray(afterSchoolEducationProgramsContent.sections) || 
        afterSchoolEducationProgramsContent.sections.length === 0) {
      throw new Error('Invalid content structure: sections missing or empty');
    }

    // Generate PDF
    console.log('Generating PDF with content'); // Debug log
    const doc = await generatePDF(
      afterSchoolEducationProgramsContent.sections,
      afterSchoolEducationProgramsContent.title,
      afterSchoolEducationProgramsContent.keywords
    );
    
    // Validate generated document
    if (!doc) {
      console.error('PDF generation returned null');
      throw new Error('PDF generation failed - null document returned');
    }

    console.log('PDF generated successfully'); // Debug log
    return doc;
  } catch (error) {
    console.error('Error in generateAfterSchoolEducationProgramsOverview:', error);
    throw new Error(`PDF generation failed: ${error.message}`);
  }
}; 